import { useEffect, useRef, useState } from 'react';

import { LoopController, clock } from '../../../lib/loop-player.js';
import { IconButton } from '../../ui/controls.js';

/** Play / Stop for a sound; it loops while playing and Stop rewinds to the start. */
export function LoopPlayer({ src, label }: { src: string; label: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctl = useRef<LoopController | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const c = new LoopController(audio);
    ctl.current = c;
    const off = c.subscribe(setPlaying);
    return () => {
      off();
      c.dispose();
      ctl.current = null;
    };
  }, [src]);

  const frac = duration > 0 ? Math.min(1, time / duration) : 0;
  return (
    <div className="gen-loop" role="group" aria-label={`${label} player`}>
      <audio
        ref={audioRef}
        preload="metadata"
        src={src}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
      />
      <IconButton icon={playing ? 'pause' : 'play'} title={playing ? 'Pause (loops while playing)' : 'Play (loops)'} active={playing} onClick={() => void ctl.current?.toggle()} />
      <IconButton
        icon="stop"
        title="Stop and rewind"
        onClick={() => {
          ctl.current?.stop();
          setTime(0);
        }}
      />
      <div className="gen-loop-track" role="progressbar" aria-label="Position" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(frac * 100)}>
        <span style={{ width: `${frac * 100}%` }} />
      </div>
      <span className="muted gen-loop-time">
        {clock(time)} / {clock(duration)}
      </span>
      <span className="muted gen-loop-tag">loop</span>
    </div>
  );
}
